/** Persistence for settings, run statistics and the run in progress. */

import { SAVE_KEY, SAVE_VERSION, TUNING } from '../core/config';
import type { SaveSystem } from '../core/systems';
import type { GameSettings, SaveData } from '../core/types';
import { makeRunStore, type RunStore } from './runsave';

/**
 * `SaveSystem` plus the run store, which `core/systems.ts` does not model.
 *
 * Widened at the factory rather than by editing the system contract, the same
 * way `makeUI` returns `UISystem & Veil`: the run snapshot belongs to the save
 * subsystem and to the level flow in `game.ts`, and neither of those owns the
 * shared interfaces. Anything that only wants a `SaveSystem` is unaffected.
 */
export interface SaveStore extends SaveSystem {
  /** The run in progress, if there is one to go back to. See `save/runsave.ts`. */
  readonly run: RunStore;
}

export function defaultSettings(): GameSettings {
  return {
    masterVolume: TUNING.audio.masterVolume,
    muted: false,
    sensitivity: TUNING.player.sensitivity,
    invertY: false,
    subtitles: true,
    render: {
      quality: 'high',
      fov: TUNING.render.defaultFov,
      postFx: true,
      headBob: true,
      resolutionScale: 1,
    },
  };
}

function defaultSave(): SaveData {
  return {
    version: SAVE_VERSION,
    settings: defaultSettings(),
    progress: { deepestLevel: 0, bestTime: 0, runs: 0, deaths: 0, notesFound: [] },
  };
}

class Storage implements SaveStore {
  data: SaveData = defaultSave();
  readonly run: RunStore = makeRunStore();

  load(): SaveData {
    try {
      const raw = localStorage.getItem(SAVE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as SaveData;
        if (parsed.version === SAVE_VERSION) {
          // Merge so settings added in a later build still get defaults.
          this.data = {
            ...defaultSave(),
            ...parsed,
            settings: { ...defaultSettings(), ...parsed.settings, render: { ...defaultSettings().render, ...parsed.settings?.render } },
            progress: { ...defaultSave().progress, ...parsed.progress },
          };
        }
      }
    } catch {
      // Corrupt or unavailable storage should never block the game.
      this.data = defaultSave();
    }
    return this.data;
  }

  save(): void {
    try {
      localStorage.setItem(SAVE_KEY, JSON.stringify(this.data));
    } catch {
      /* private mode, quota, etc. — non-fatal */
    }
  }

  updateSettings(patch: Partial<GameSettings>): GameSettings {
    this.data.settings = { ...this.data.settings, ...patch };
    this.save();
    return this.data.settings;
  }

  recordRun(result: { survived: number; depth: number; died: boolean }): void {
    const p = this.data.progress;
    p.runs++;
    if (result.died) p.deaths++;
    p.bestTime = Math.max(p.bestTime, result.survived);
    p.deepestLevel = Math.max(p.deepestLevel, result.depth);
    this.save();
  }

  markNoteFound(id: string): void {
    if (!this.data.progress.notesFound.includes(id)) {
      this.data.progress.notesFound.push(id);
      this.save();
    }
  }

  reset(): void {
    this.data = defaultSave();
    // Wiping the save wipes the run with it. Leaving a resumable run attached
    // to a record that says nobody has ever played is the sort of state that
    // only ever turns into a bug report.
    this.run.clear();
    this.save();
  }
}

export function makeSaveSystem(): SaveStore {
  return new Storage();
}
